/******/ (() => { // webpackBootstrap
/*!***************************************************************!*\
  !*** ./_build/scripts/snippets/frequently-bought-together.js ***!
  \***************************************************************/
class FrequentlyBoughtTogether extends HTMLElement {
  constructor() {
    super();
    this.initAddToCart();
    this.initPriceUpdates();
    this.initVariantDropdowns();
    this.initDrawer();
  }

  connectedCallback() {
    if (this._hasBeenInitialised) return;
    this._hasBeenInitialised = true;
    this.initCompProducts();
  }

  initCompProducts() {
    const url = this.dataset.url;
    const desiredMode = this.dataset.mode; // 'full' or 'compact'

    if (!url) return;

    fetch(url)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;

        const fresh =
          html.querySelector(`frequently-bought-together[data-fbt][data-mode="${desiredMode}"]`) ||
          html.querySelector('frequently-bought-together[data-fbt]') ||
          html.querySelector('frequently-bought-together');

        if (fresh && fresh.innerHTML.trim().length) {
          this.innerHTML = fresh.innerHTML;

          const compactBtn = this.closest('.fbt-compact');
          if (compactBtn) compactBtn.removeAttribute('hidden');

          this.initAddToCart();
          this.initPriceUpdates();
          this.initVariantDropdowns();
          this.initDrawer();
          this.updatePrice();
        } else {
          (this.closest('.fbt-compact') || this).remove();
        }
      })
      .catch((e) => console.error(e));
  }

  initVariantDropdowns() {
    // If you re-render, this runs again, so avoid double-binding per select
    const selects = Array.from(this.querySelectorAll('.variant-size__select'));
    selects.forEach((select) => {
      if (select._fbtBound) return;
      select._fbtBound = true;

      select.addEventListener('change', (e) => {
        const sel = e.currentTarget;

        // Find the related checkbox inside the same fbt item card
        const wrap = sel.closest('.fbt-item');
        if (!wrap) return;

        const checkbox = wrap.querySelector('[data-fbt-item-checkbox]');
        if (!checkbox) return;

        const newVariantId = sel.value;
        const opt = sel.options[sel.selectedIndex];

        // ✅ This is what initAddToCart reads:
        checkbox.dataset.fbtItem = newVariantId;
        checkbox.setAttribute('data-fbt-item', newVariantId);

        // ✅ This is what updatePrice reads:
        const newPricePence = opt?.dataset?.pricePence;
        if (newPricePence != null) {
          checkbox.dataset.fbtPricePence = String(newPricePence);
          checkbox.setAttribute('data-fbt-price-pence', String(newPricePence));
        }

        // Optional but nice: keep checkbox id/label "for" valid
        const label = wrap.querySelector('label[for^="bundle-add-"]');
        checkbox.id = `bundle-add-${newVariantId}`;
        if (label) label.setAttribute('for', checkbox.id);

        // If item is currently selected, update totals immediately
        if (checkbox.checked) this.updatePrice();
      }, { passive: true });
    });
  }

  initPriceUpdates() {
    const bundleItems = Array.from(this.querySelectorAll('[data-fbt-item-checkbox]'));

    bundleItems.forEach((item) => {
      item.addEventListener('change', () => this.updatePrice(), { passive: true });
    });
  }

  updatePrice() {
    const bundleItems = Array.from(this.querySelectorAll('[data-fbt-item-checkbox]'));
    let totalPrice = 0;

    bundleItems.forEach((item) => {
      if (item.checked) {
        const price = Number(item.dataset.fbtPricePence);
        totalPrice += price;
      }
    });

    const totalPriceElement = this.querySelector('[data-fbt-total-price]');
    if (totalPriceElement) {
      totalPriceElement.innerHTML = this._formatMoney(totalPrice, '£{{amount}}');
    }
  }

  initAddToCart() {
    const button = this.querySelector('[data-fbt-add]');
    if (!button) return;

    if (button._fbtBound) return;
    button._fbtBound = true;

    button.addEventListener('click', async () => {
      const bundleElementsToAdd = Array.from(this.querySelectorAll('[data-fbt-item]'))
        .filter((item) => item.checked);

      const bundleIds = bundleElementsToAdd.map((item) => item.dataset.fbtItem);

      if (bundleIds.length > 0) {
        const sparkItems = bundleIds.map((id) => ({
          id: Number(id),
          quantity: 1,
          properties: { _source: 'fbt' }
        }));

        try {
          await this.addToCart(sparkItems);
        } catch (e) {}

        window.location.assign('/cart');
      }
    });
  }

  async addToCart(items) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.description || 'Could not add to cart');
    }

    return response.json();
  }

  _formatMoney(amount) {
    if (window.Herd?.utils?.formatMoney) {
      return window.Herd.utils.formatMoney(amount, '£{{amount}}');
    }
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(amount, window.Shopify.money_format || '£{{amount}}');
    }
    return '£' + (amount / 100).toFixed(2);
  }

  initDrawer() {
    const openBtn = this.querySelector('[data-fbt-open-drawer]');
    if (!openBtn || openBtn._fbtDrawerBound) return;
    openBtn._fbtDrawerBound = true;

    const drawer = document.querySelector('[data-fbt-drawer]');
    if (!drawer) return;

    openBtn.addEventListener('click', () => this.openDrawer(drawer));

    if (!drawer._fbtBound) {
      drawer._fbtBound = true;

      // Move drawer and overlay to body so they escape any parent stacking context
      const overlay = document.querySelector('[data-fbt-drawer-external-el="overlay"]');
      document.body.appendChild(drawer);
      if (overlay) document.body.appendChild(overlay);

      const closeBtn = drawer.querySelector('[data-fbt-drawer-el="close"]');
      if (closeBtn) closeBtn.addEventListener('click', () => this.closeDrawer(drawer));

      const addBtn = drawer.querySelector('[data-fbt-drawer-add]');
      if (addBtn) addBtn.addEventListener('click', () => this.addDrawerItemsToCart(drawer));

      // Close on any click outside the drawer — more reliable than overlay-only
      document.addEventListener('pointerdown', (e) => {
        if (drawer.hasAttribute('data-is-open') && !drawer.contains(e.target)) {
          this.closeDrawer(drawer);
        }
      }, { capture: true });
    }
  }

  openDrawer(drawer) {
    this.populateDrawer(drawer);
    drawer.setAttribute('data-is-open', '');
    const overlay = document.querySelector('[data-fbt-drawer-external-el="overlay"]');
    if (overlay) overlay.setAttribute('data-is-open', '');
  }

  closeDrawer(drawer) {
    drawer.removeAttribute('data-is-open');
    const overlay = document.querySelector('[data-fbt-drawer-external-el="overlay"]');
    if (overlay) overlay.removeAttribute('data-is-open');
  }

  populateDrawer(drawer) {
    const itemsContainer = drawer.querySelector('[data-fbt-drawer-items]');
    if (!itemsContainer) return;

    itemsContainer.innerHTML = '';

    Array.from(this.querySelectorAll('.fbt-item')).forEach((fbtItem, index) => {
      let variants = [];
      try { variants = JSON.parse(fbtItem.dataset.fbtVariants || '[]'); } catch (e) {}

      const detailsEl = fbtItem.querySelector('.variant-details');
      if (!detailsEl) return;

      const wrapper = document.createElement('div');
      wrapper.className = 'fbt-item';
      wrapper.dataset.fbtDrawerItemIndex = String(index);
      wrapper.dataset.selectedVariantId = '';
      wrapper.dataset.selectedVariantPrice = '';

      const img = fbtItem.querySelector('img.featured-img');
      if (img) wrapper.appendChild(img.cloneNode(true));

      const cloned = detailsEl.cloneNode(true);
      cloned.removeAttribute('hidden');
      wrapper.appendChild(cloned);

      itemsContainer.appendChild(wrapper);
      this.initDrawerItemVariants(wrapper, variants);
    });

    this.updateDrawerTotal(drawer);
  }

  initDrawerItemVariants(wrapper, variants) {
    wrapper._fbtVariants = variants;
    wrapper.dataset.baseVariantPrice = String(variants[0]?.price || 0);

    const selects = Array.from(wrapper.querySelectorAll('[data-fbt-option-select]'));

    const syncVariant = () => {
      let matched = null;

      if (selects.length === 0) {
        matched = variants[0] || null;
      } else {
        const selectedValues = selects.map((s) => s.value);
        if (selectedValues.every((v) => v !== '')) {
          matched = variants.find((v) =>
            selectedValues.every((val, i) => v.options[i] === val)
          ) || null;
        }
      }

      const priceEl = wrapper.querySelector('[data-fbt-item-price]');
      if (priceEl && matched) {
        priceEl.textContent = this._formatMoney(matched.price, '£{{amount}}');
      }

      wrapper.dataset.selectedVariantId = matched ? String(matched.id) : '';
      wrapper.dataset.selectedVariantPrice = matched ? String(matched.price) : '';

      const variantDetails = wrapper.querySelector('.variant-details');
      if (matched && !matched.available) {
        wrapper.dataset.fbtError = '';
        if (variantDetails) variantDetails.dataset.fbtErrorMessage = 'Size not in stock';
      } else {
        delete wrapper.dataset.fbtError;
        if (variantDetails) delete variantDetails.dataset.fbtErrorMessage;
      }

      const drawer = wrapper.closest('[data-fbt-drawer]');
      if (drawer) this.updateDrawerTotal(drawer);
    };

    selects.forEach((select) => select.addEventListener('change', syncVariant));

    const checkbox = wrapper.querySelector('[data-fbt-item-checkbox]');
    if (checkbox) {
      checkbox.addEventListener('change', () => {
        const drawer = wrapper.closest('[data-fbt-drawer]');
        if (drawer) this.updateDrawerTotal(drawer);
      });
    }

    syncVariant();
  }

  updateDrawerTotal(drawer) {
    const itemsContainer = drawer.querySelector('[data-fbt-drawer-items]');
    if (!itemsContainer) return;

    let total = 0;
    itemsContainer.querySelectorAll('[data-fbt-drawer-item-index]').forEach((item) => {
      const checkbox = item.querySelector('[data-fbt-item-checkbox]');
      if (checkbox && !checkbox.checked) return;
      const price = Number(item.dataset.selectedVariantPrice) || Number(item.dataset.baseVariantPrice) || 0;
      total += price;
    });

    const totalEl = drawer.querySelector('[data-fbt-drawer-total]');
    if (totalEl) totalEl.textContent = this._formatMoney(total, '£{{amount}}');
  }

  async addDrawerItemsToCart(drawer) {
    const errorEl = drawer.querySelector('[data-fbt-drawer-error]');
    if (errorEl) errorEl.textContent = '';

    const itemsContainer = drawer.querySelector('[data-fbt-drawer-items]');
    if (!itemsContainer) return;

    const allDrawerItems = Array.from(itemsContainer.querySelectorAll('[data-fbt-drawer-item-index]'));
    const drawerItems = allDrawerItems.filter((item) => {
      const checkbox = item.querySelector('[data-fbt-item-checkbox]');
      return !checkbox || checkbox.checked;
    });

    const unselected = drawerItems.filter((item) => !item.dataset.selectedVariantId);
    if (unselected.length) {
      if (errorEl) errorEl.textContent = 'Please select a size/variant for each item.';
      return;
    }

    const outOfStock = drawerItems.filter((item) => {
      const selectedId = Number(item.dataset.selectedVariantId);
      const variant = item._fbtVariants?.find((v) => v.id === selectedId);
      return variant && !variant.available;
    });

    if (outOfStock.length) {
      const names = outOfStock.map((item) => item.querySelector('.title')?.textContent?.trim() || 'an item');
      if (errorEl) errorEl.textContent = `Out of stock: ${names.join(', ')}.`;
      return;
    }

    const items = drawerItems.map((item) => ({
      id: Number(item.dataset.selectedVariantId),
      quantity: 1,
      properties: { _source: 'fbt' }
    }));

    const addBtn = drawer.querySelector('[data-fbt-drawer-add]');
    if (addBtn) addBtn.setAttribute('disabled', '');

    try {
      await this.addToCart(items);
      this.closeDrawer(drawer);
      window.location.assign('/cart');
    } catch (e) {
      if (errorEl) errorEl.textContent = 'Could not add items to cart. Please try again.';
      if (addBtn) addBtn.removeAttribute('disabled');
    }
  }
}

customElements.define('frequently-bought-together', FrequentlyBoughtTogether);

/******/ })()
;